import { Injectable, Injector } from '@angular/core';
import { Platform, NavController } from '@ionic/angular';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { BehaviorSubject, Observable, Subscription, from } from 'rxjs';
import { IdbService } from './indexdb.service';
import { StorageService } from './core/storage.service';
import { LoginService } from './login.service';
import { ApiService } from './core/api.service';
import { FingerprintAIO } from '@ionic-native/fingerprint-aio/ngx';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class AuthenticateService {
  isPromptShowing: boolean = false;
  loadDs: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  // 🟢 NEW: Inactivity Tracking
  // We initialize with Date.now() but will load from DB in constructor/init
  private lastActiveTime: number = Date.now();
  private readonly INACTIVITY_LIMIT_MS = 5 * 60 * 1000; // 5 Minutes

  constructor(private platform: Platform, private idb_service: IdbService, private faio: FingerprintAIO, private storage_service: StorageService, private injector: Injector) {
    // 🟢 NEW: Load persisted time on startup
    this.platform.ready().then(async () => {
      this.lastActiveTime = await this.idb_service.getLastActiveTime() || Date.now();
    });
  }

  // Lazy load to be 100% safe against circular deps
  private get api_service() {
    return this.injector.get(ApiService);
  }


  openSetting() {
    if (this.platform.is('android')) {
      NativeSettings.openAndroid({
        option: AndroidSettings.Security,
      });
    }
    else if (this.platform.is('ios')) {
      this.checkDsData().subscribe((_data: string) => {
        if (_data == 'BIOMETRIC_NOT_SET') {
          NativeSettings.openIOS({
            option: IOSSettings.TouchIdPasscode
          });
        }
        else if (_data == 'BIOMETRIC_NOT_AVAILABLE') {
          NativeSettings.openIOS({
            option: IOSSettings.App
          });
        }
      });
    }
  }

  checkDsData(): Observable<string> {
    return this.loadDs.asObservable();
  }

  handleError(error) {
    if (this.platform.is('android')) {
      if (error.message == 'BIOMETRIC_NOT_ENROLLED' && error.code == '-106') {
        // 🟢 FIX: Suppress error, let Login Page handle enrollment
       // console.log('Biometric not enrolled on device.');
        // this.loadDs.next('BIOMETRIC_NOT_SET'); 
        return;
      }
      else if (error.message == 'BIOMETRIC_NO_SECRET_FOUND' && error.code == '-113') {
        this.loadDs.next('BIOMETRIC_SECRET_NOT_SET');
      }
      else if (error.message == 'BIOMETRIC_DISMISSED' && error.code == '-108') {
        this.loadDs.next('BIOMETRIC_DISMISSED')
      }
      else if (error.message == 'BIOMETRIC_LOCKED_OUT' && error.code == '-102') {
        this.loadDs.next('BIOMETRIC_LOCKED_OUT');
      }
      else if (error.message == 'BIOMETRIC_LOCKED_OUT_PERMANENT' && error.code == '-103') {
        this.loadDs.next('BIOMETRIC_LOCKED_OUT_PERMANENT');
      }
      else if (error.code == '-111') {
        // PIN/Pattern dismissed
        this.loadDs.next('BIOMETRIC_DISMISSED');
      }
      else {
        this.loadDs.next('UNKNOWN_BIOMETRIC_ERROR');
      }
      // else if(error.message=='BIOMETRIC_DISMISSED' && error.code=='-108'){
      //   this.loadDs.next('BIOMETRIC_SECRET_NOT_SET');
      // }
    }
    else if (this.platform.is('ios')) {
      if (error.message == 'Biometry is not enrolled.' && error.code == '-106') {
        this.loadDs.next('BIOMETRIC_NOT_SET');
      }
      else if (error.message == 'Biometry is not available.' && error.code == '-101') {
        this.loadDs.next('BIOMETRIC_NOT_AVAILABLE');
      }
      else if (error.message == 'Authentication canceled.' && error.code == '-108') {
        this.loadDs.next('BIOMETRIC_DISMISSED')
      }
      else if (error.message == 'No secret found.' || error.code == '-113') {
        this.loadDs.next('BIOMETRIC_SECRET_NOT_SET');
      }
      else if (error.code == '-102') {
        // Locked out
        this.loadDs.next('BIOMETRIC_LOCKED_OUT');
      }
      else {
        this.loadDs.next('UNKNOWN_BIOMETRIC_ERROR');
      }
    }
  }

  async validateToken() {
    let _token = await this.idb_service.getTkn();
    let _rConfig = await this.idb_service.getRTkn();
    
    if (!_token || !_rConfig?.RefreshToken) {
      return false;
    }

    // 🛡️ Proactive Expiry Check: Access Token expires in 20 minutes on the server.
    // We check against 18 minutes locally to match the background refresh timer and account for network/clock skew.
    const lastRefresh = await this.idb_service.getLastRefreshedTime();
    if (lastRefresh === 0) return false;

    const now = Date.now();
    const eighteenMinsInMs = 18 * 60 * 1000;
    const isExpired = (now - lastRefresh) >= eighteenMinsInMs;

    return !isExpired;
  }

  // 🛡️ Identity Switch Guard: Wipe everything if a different user logs in
  async checkAndResetIfUserSwitched(newUsername: string): Promise<void> {
    if (!newUsername) return;

    const lastUser = (await this.storage_service.getString('LAST_LOGGED_IN_USER'))?.value;

    if (lastUser && lastUser.toLowerCase() !== newUsername.toLowerCase()) {
      console.log(`Identity switch detected: ${lastUser} -> ${newUsername}. Performing security wipe.`);
      
      // 🔒 Wipe all stores to prevent cross-user data leakage (No caching)
      await this.idb_service.wipeAllDataForNewUser();
      
      // Clear memory caches of services for safety
      const loginService = this.injector.get(LoginService);
      if (loginService) {
        loginService.mfPortfolio = null;
        loginService.orderBookData = null;
        loginService.categoryList = null;
      }
    }

    await this.storage_service.setString('LAST_LOGGED_IN_USER', newUsername);
  }

  // 🟢 NEW: Enrollment Check (Call AFTER login)
  async shouldPromptEnrollment(): Promise<boolean> {
    // 🛡️ Guard: Never prompt enrollment on web platform
    if (Capacitor.getPlatform() === 'web') return false;

    const state = await this.idb_service.getBiometricState();
    // If not enabled, always prompt (Persistent Nagging)
    return !state.isEnabled;
  }

  // 🟢 NEW: Centralized Inactivity Check (Call on Resume)
  async checkSessionStatus(): Promise<void> {
    // 🛡️ GUARD: If the user is fully logged out (no tokens), do NOT prompt automatically
    const aToken = await this.idb_service.getTkn();
    const rConfig = await this.idb_service.getRTkn();
    if (!aToken && !rConfig?.RefreshToken) {
      return;
    }

    const now = Date.now();
    const inactiveDuration = now - this.lastActiveTime;

    // 1. Session Valid ( < 1 min ) - No prompt needed
    if (inactiveDuration < this.INACTIVITY_LIMIT_MS) {
      return;
    }

    // 2. Intermediate or Expired Session ( > 1 min )
    const state = await this.idb_service.getBiometricState();
    const isTokenValid = await this.validateToken();

    if (state.isEnabled && state.secretStored) {
      // 🚀 Delay prompt slightly on resume to ensure the app window has focus and plugins are ready!
      // This prevents the OS from instantly rejecting the prompt with "Authentication canceled" due to focus lag!
      await new Promise(resolve => setTimeout(resolve, 800));

      const success = await this.showBiometricPrompt('Authenticate to Resume', 'Please authenticate to resume your session');
      
      if (success) {
        // 🟢 Force a data refresh whenever biometric succeeds, keeping data perfectly in sync!
        this.idb_service.triggerPortfolioRefresh();

        if (isTokenValid) {
          // Session still valid
        } else {
          const refreshed = await this.api_service.refreshAccessTokenManually(true);
          if (!refreshed) {
            // Check if the Refresh Token is still present in the store
            const retryToken = await this.idb_service.getRTkn();
            if (!retryToken?.RefreshToken) {
              // Terminal Auth Error (token was wiped because it actually expired after 90 days)
              const reauthSuccess = await this.attemptSilentReAuth();
              if (!reauthSuccess) {
                const nav = this.injector.get(NavController);
                nav.navigateRoot(['/login-via-otp']);
              }
            } else {
              // Non-terminal Network Error: Refresh Token is still present! Keep the user's session active.
              console.log('🔄 Resume: Refresh failed due to network issue. Session remains active.');
            }
          }
        }
      } else {
        // If biometric prompt was dismissed or failed, don't clear their credentials or wipe their DB!
        // Just send them back to the login page where they can trigger biometrics again when ready.
        const nav = this.injector.get(NavController);
        nav.navigateRoot(['/login-via-otp']);
      }
    } else {
      if (!isTokenValid) {
        const nav = this.injector.get(NavController);
        nav.navigateRoot(['/login-via-otp']);
      }
    }
  }

  public async performSilentReauth(loginFilter: { l: string, p: string }) {
    try {
      // Lazy load LoginService to avoid circular dependency
      const loginService = this.injector.get(LoginService);
      const response = await loginService.getLoginResponses(loginFilter);
      if (response && response.Data) {
        await this.idb_service.setLoginTokenData(response.Data);
        await this.api_service.dataRefreshTokenFn();
        return response.Data;
      }
    } catch (error) {
      console.error('Silent re-auth failed:', error);
    }
    return null;
  }

  private async attemptSilentReAuth(): Promise<boolean> {
    const loginCred = await this.storage_service.getLoginCred();
    if (!loginCred) {
      return false;
    }

    const loginFilter = { l: loginCred.name, p: loginCred.pswd };
    const success = await this.performSilentReauth(loginFilter);
    return !!success;
  }

  updateLastActiveTime() {
    this.lastActiveTime = Date.now();
    // 🟢 NEW: Persist to DB
    this.idb_service.saveLastActiveTime(this.lastActiveTime);
  }

  private triggerLogout() {
    this.loadDs.next('FORCE_LOGOUT');
  }

  async showBiometricPrompt(title: string = 'Login to Purse+', subtitle: string = 'Authenticate to login'): Promise<boolean> {
    if (Capacitor.getPlatform() === 'web') return false;

    // 🛡️ Prevent overlapping prompts
    if (this.isPromptShowing) return false;

    try {
      this.isPromptShowing = true; // 🔒 LOCK
      await this.idb_service.setBiometricState({ isInvoking: true });

      const result = await this.faio.loadBiometricSecret({
        title: title,
        subtitle: subtitle,
        description: Capacitor.getPlatform() === 'ios' ? 'Face ID access is required' : '',
        disableBackup: true,
      });

      if (result) {
        await this.idb_service.setBiometricState({
          isInvoking: false,
          secretStored: true,
          isEnabled: true,
          isDismissed: false
        });
        return true;
      }
      return false;
    } catch (error) {
      await this.idb_service.setBiometricState({ isInvoking: false });
      this.handleError(error);
      return false;
    } finally {
      this.isPromptShowing = false; // 🔓 ALWAYS UNLOCK
    }
  }

  async registerBiometricSecret(secret: string = 'my-super-secret'): Promise<boolean> {
    if (Capacitor.getPlatform() === 'web') return false;
    try {
      await this.idb_service.setBiometricState({ isInvoking: true });
      await this.faio.isAvailable();
      await this.faio.registerBiometricSecret({
        title: 'Register Biometrics',
        subtitle: 'Authenticate to enable biometric login',
        secret: secret,
        invalidateOnEnrollment: true,
        disableBackup: true,
      });
      await this.idb_service.setBiometricState({ isInvoking: false, isEnabled: true, secretStored: true, isDismissed: false, isSkipped: false });
      return true;
    } catch (error) {
      await this.idb_service.setBiometricState({ isInvoking: false });
      this.handleError(error);
      return false;
    }
  }

}